import pick from 'latest_modules/lodash/pick';
import shoppingCarts from '../schema/ShoppingCarts';
import shoppingCartApi from '../../api/shoppingcarts';
import savedForLaterApi from '../../api/savedForLater';
import { SHOPPING_CART as shoppingCartTypes } from '../mutation-types';

let EXPIRATION_TIMEOUT_ID = null;

shoppingCarts.state.cartId = null;

const getters = Object.assign(shoppingCarts.getters, {
  current(state) {
    return (options = {}) => {
      return shoppingCarts.findOne(
        {
          _id: state.cartId
        },
        options
      );
    };
  }
});

const mutations = {
  // GAのプロセス送信用に必要
  [shoppingCartTypes.ENABLE_TO_PURCHASE]() {},

  [shoppingCartTypes.DATA_RECEIVED](state, payload) {
    state.cartId = payload._id;

    payload.discount = payload.discount && payload.discount._id;

    shoppingCarts.updateById(state.cartId, payload, {
      upsert: true
    });
  },

  [shoppingCartTypes.UPDATE_PRICE](state, payload) {
    const prices = pick(payload.cart, [
      'product_subtotal',
      'final_subtotal',
      'discount',
      'gift_wrapping_unit_price',
      'discount_real_price_with_coupon',
      'discount_price_with_coupon',
      'gift_wrapping_charge',
      'show_acquired_point_rate_notice',
      'shipping_charge',
      'reserved_point',
      'acquire_point',
      'acquired_point_rate',
      'subtotal',
      'premium_discountable_price_total',
      'sku_premium_saving_price_total',
      'grand_total',
      'grand_total_for_cart_page',
      'point',
      'max_usable_tom_point',
      'sku_discount_price_total',
      'sku_premium_discount_price_total',
      'premium_will_gain_recontract_extra_discount',
      'premium_assumed_will_gain_recontract_extra_discount',
      'premium_subscription_bonus_point',
      'import_fees'
    ]);

    prices.discount = prices.discount && prices.discount._id;
    shoppingCarts.updateById(state.cartId, prices);
  },

  [shoppingCartTypes.UPDATE_SHIPMENTS](state, payload) {
    shoppingCarts.updateById(state.cartId, {
      shipments: payload.shipments
    });
  },

  [shoppingCartTypes.UPDATE_PREMIUM](state, payload) {
    const cart = shoppingCarts.findOne({
      _id: state.cartId
    });

    if (cart) {
      const premium = Object.assign(cart.premium, payload.premium);
      shoppingCarts.updateById(state.cartId, {
        premium
      });
    }
  },

  [shoppingCartTypes.UPDATE_RECONTRACT_EXTRA_POINT](state, payload) {
    const cart = shoppingCarts.findOne({
      _id: state.cartId
    });

    if (cart) {
      shoppingCarts.updateById(state.cartId, {
        premium_will_gain_recontract_extra_discount:
          payload.premium_will_gain_recontract_extra_discount,
        premium_assumed_will_gain_recontract_extra_discount:
          payload.premium_assumed_will_gain_recontract_extra_discount
      });
    }
  },

  /**
   * カート有効期限までの残り秒数を更新する
   * カウントダウン時にも呼び出される
   * @param state
   * @param payload
   */
  [shoppingCartTypes.UPDATE_EXPIRATION_SECONDS](state, payload) {
    shoppingCarts.updateById(state.cartId, {
      expiration_seconds: payload.expiration_seconds
    });
  }
};

// Actions
// -----------------------------
const actions = {
  fetch({ dispatch, commit }, payload) {
    return new Promise(function (resolve, reject) {
      shoppingCartApi.cart(payload, function (err, res) {
        if (err) {
          return reject(err);
        }

        let cartData = res.cart;
        if (!cartData) return resolve(res);

        dispatch('premiumPlans/insert', res.cart.premium.plan, {
          root: true
        });

        cartData.premium.plan = res.cart.premium.plan._id;

        dispatch(
          'shipments/update',
          {
            shipments: res.cart.shipments,
            shipmentMap: res.shipmentMap,
            shippingChargesMap: res.shippingChargesMap
          },
          {
            root: true
          }
        );

        cartData.shipments = res.cart.shipments.map((shipment) => {
          return shipment._id;
        });

        // クーポンが適用されている場合
        if (cartData.discount) {
          dispatch('shoppingDiscounts/insert', cartData.discount, {
            root: true
          });
        }

        if (res.cart && res.premium) {
          const possiblePremiumPlanAndType =
            res.premium.possiblePremiumPlanAndType;

          dispatch(
            'user/willJoinPremium',
            {
              join: res.cart.premium.is_joining
            },
            {
              root: true
            }
          );

          dispatch(
            'user/hasWaitingAutoRecurringPremium',
            {
              has_waiting_auto_recurring_premium:
                res.premium.hasWaitingAutoRecurringPremium
            },
            {
              root: true
            }
          );

          dispatch(
            'user/possiblePremiumPlanType',
            {
              possible_premium_plan_type: possiblePremiumPlanAndType
                ? possiblePremiumPlanAndType.plan_type
                : null
            },
            {
              root: true
            }
          );

          dispatch(
            'user/hadRegisteredPremiumInPast',
            {
              had_registered_premium_in_past:
                res.premium.hadRegisteredPremiumInPast
            },
            {
              root: true
            }
          );

          dispatch(
            'user/setChannelId',
            {
              channel_id: res.premium.channelId ? res.premium.channelId : null
            },
            {
              root: true
            }
          );

          dispatch(
            'user/currentSubscriptionId',
            {
              current_subscription_id: res.premium.currentSubscriptionId
                ? res.premium.currentSubscriptionId
                : null
            },
            {
              root: true
            }
          );

          dispatch(
            'user/canBuyWithAnnualPlan',
            {
              can_buy_with_annual_plan: res.premium.canBuyWithAnnualPlan
                ? res.premium.canBuyWithAnnualPlan
                : null
            },
            {
              root: true
            }
          );
        }

        commit(shoppingCartTypes.DATA_RECEIVED, cartData);

        // 有効期限を更新
        // カートが空の場合のリダイレクト処理はルーター側で処理しているため、
        // updateCountdownToExpirationを実行させないようにする
        // なお、リダイレクト処理は`expiration_seconds`の変更を監視している下記処理内で行う
        // assets/vue/containers/checkout/index.jsの`store.subscribe...`部分
        if (cartData.expiration_seconds) {
          dispatch('updateCountdownToExpiration', {
            expiration_seconds: cartData.expiration_seconds
          });
        }

        return resolve(res);
      });
    });
  },

  /**
   * カウントダウンをストップ
   */
  stopCountdownToExpiration() {
    clearTimeout(EXPIRATION_TIMEOUT_ID);
  },

  /**
   * カートの有効期限をセットしカウントダウンを始める
   * @param payload._id Cart id
   * @param payload.expiration_seconds 有効期限までの秒数
   */
  updateCountdownToExpiration({ getters, commit, dispatch }, payload) {
    commit(shoppingCartTypes.UPDATE_EXPIRATION_SECONDS, {
      expiration_seconds: payload.expiration_seconds
    });
    clearTimeout(EXPIRATION_TIMEOUT_ID);
    EXPIRATION_TIMEOUT_ID = setTimeout(() => {
      const cart = getters.current();
      const expiration_seconds = cart.expiration_seconds - 1;
      if (expiration_seconds >= 0) {
        dispatch('updateCountdownToExpiration', {
          expiration_seconds
        });
      }
    }, 1000);
  },

  updatePrice({ commit }, payload) {
    commit(shoppingCartTypes.UPDATE_PRICE, {
      cart: payload.cart
    });
  },

  enebleToPurchase({ commit }) {
    commit(shoppingCartTypes.ENABLE_TO_PURCHASE);
  },

  qty({ getters, commit, dispatch }, payload) {
    return new Promise(function (resolve, reject) {
      return shoppingCartApi.qty(payload, function (err, res) {
        if (err) {
          return reject(err);
        }

        dispatch(
          'shipments/update',
          {
            shipments: res.cart.shipments,
            shipmentMap: res.shipmentMap,
            shippingChargesMap: res.shippingChargesMap
          },
          {
            root: true
          }
        );

        commit(shoppingCartTypes.UPDATE_SHIPMENTS, {
          shipments: res.cart.shipments.map((shipment) => {
            return shipment._id;
          })
        });

        commit(shoppingCartTypes.UPDATE_PRICE, {
          cart: res.cart
        });

        // GAのプロセス送信用に必要
        commit(shoppingCartTypes.ENABLE_TO_PURCHASE);

        // カートが空になった場合はカウントダウンをストップさせる
        // updateCountdownToExpiration経由でリダイレクト処理をしてしまうと
        // /cart?err=cart_expiredにリダイレクトしてしまうため
        // なお、リダイレクト処理は`cart.shipments`の変更を監視している下記処理内で行う
        // assets/vue/containers/checkout/index.jsの`store.subscribe...`部分
        if (shoppingCarts.isEmpty(getters.current())) {
          dispatch('stopCountdownToExpiration');
        } else {
          // 有効期限を更新
          dispatch('updateCountdownToExpiration', {
            expiration_seconds: res.cart.expiration_seconds
          });
        }

        return resolve(res);
      });
    });
  },

  /*
  Add items to the shopping cart
  @param {Object} payload
  @param {String} payload.product_id - Product id
  @param {String} payload.sku_id - Product sku id
  @param {String} payload.wishlist_id - Wish list id
  */
  addToCart(context, payload) {
    return new Promise(function (resolve, reject) {
      return shoppingCartApi.addToCart(
        {
          sku_id: payload.sku_id,
          product_id: payload.product_id,
          wishlist_id: payload.wishlist_id
        },
        function (err) {
          if (err) {
            return reject(err);
          }

          if (payload.ga) {
            // GA
            window.tom.helper.ga.push([
              'event',
              'EC',
              payload.ga.action,
              payload.sku_id,
              1
            ]);
            window.tom.helper.ga.addToCart(payload.ga.impression);
          }

          return resolve();
        }
      );
    });
  },

  /*
  Change flag to join TOM Premium
  @param {Object} payload
  @param {Boolean} payload.join - Will join to TOM Premium or not
  */
  joinPremium({ commit, dispatch, getters, state }, payload = {}) {
    return new Promise(function (resolve, reject) {
      if (typeof payload.join !== 'boolean') {
        return reject(new Error('Type error'));
      }

      commit(shoppingCartTypes.UPDATE_PREMIUM, {
        premium: {
          is_joining: payload.join
        }
      });

      shoppingCartApi.joinPremium(
        {
          join: payload.join,
          wishlist_id: payload.wishlist_id
        },
        function (err, res) {
          if (err) {
            const returnErr = new Error(
              `Sorry, an error occurred while trying to join TOM Premium. Please try again later. ${
                err.message || ''
              }`
            );

            returnErr.type = 'joinPremiumError';

            // カート・チェックアウトフローでのみ処理する
            if (state.cartId) {
              commit(shoppingCartTypes.UPDATE_PREMIUM, {
                premium: {
                  is_joining: !payload.join
                }
              });
            }

            return reject(returnErr);
          }

          // カート・チェックアウトフローでのみ処理する
          if (state.cartId) {
            const cart = getters.current({
              populate: ['shipments']
            });

            // populateしたcart.shipmentsをDeepコピー
            const shipments = JSON.parse(JSON.stringify(cart.shipments));

            dispatch(
              'shipments/update',
              {
                shipments: shipments,
                shippingChargesMap: res.shippingChargesMap
              },
              {
                root: true
              }
            );

            commit(shoppingCartTypes.UPDATE_PRICE, {
              cart: res.prices
            });

            const updatePremiumOptions = {
              can_get_bonus: res.premium.can_get_bonus
            };

            if (res.premium.plan_type) {
              dispatch(
                'user/possiblePremiumPlanType',
                {
                  possible_premium_plan_type: res.premium.plan_type
                },
                {
                  root: true
                }
              );

              updatePremiumOptions.possible_premium_plan_type =
                res.premium.plan_type;
            }

            commit(shoppingCartTypes.UPDATE_PREMIUM, {
              premium: updatePremiumOptions
            });

            commit(shoppingCartTypes.UPDATE_RECONTRACT_EXTRA_POINT, {
              premium_will_gain_recontract_extra_discount:
                res.premium_will_gain_recontract_extra_discount,
              premium_assumed_will_gain_recontract_extra_discount:
                res.premium_assumed_will_gain_recontract_extra_discount
            });

            // GAのプロセス送信用に必要
            commit(shoppingCartTypes.ENABLE_TO_PURCHASE);

            // 有効期限を更新
            dispatch('updateCountdownToExpiration', {
              expiration_seconds: res.expiration_seconds
            });
          }

          resolve(res);
        }
      );
    });
  },

  /*
  Fetch selectable shipping methods
  @param {Object} payload
  @param {Object} payload.shipmentId - cart.shipments.id, not Shipments._id
  @param {Object} payload.shippingId - Shipping._id
  */
  fetchSelectableShippingMethods(context, payload) {
    return new Promise(function (resolve, reject) {
      shoppingCartApi.fetchSelectableShippingMethods(
        {
          shipmentId: payload.shipmentId,
          shippingId: payload.shippingId,
          shippingAddress: payload.shippingAddress
        },
        function (err, res) {
          if (err) {
            return reject(err);
          }
          resolve(res);
        }
      );
    });
  },

  /*
  Clear in-cart items.
  */
  clearCart(context, payload = {}) {
    return new Promise(function (resolve, reject) {
      shoppingCartApi.clearCart(payload, function (err, res) {
        if (err) {
          return reject(err);
        }
        resolve(res);
      });
    });
  },

  /*
  Checkout in-cart items.
  */
  checkout(context, payload) {
    return new Promise(function (resolve, reject) {
      shoppingCartApi.checkout(payload, function (err, res) {
        if (err) {
          return reject(err);
        }
        resolve(res);
      });
    });
  },

  /*
  Checkout in-cart items. (PayPal)
  */
  checkoutWithPaypal(context, payload) {
    return new Promise(function (resolve, reject) {
      shoppingCartApi.checkoutWithPaypal(payload, function (err, res) {
        if (err) {
          return reject(err);
        }
        resolve(res);
      });
    });
  },

  /*
  Change shipping method for the shipment of the shipmentId.
  @param {Object} payload
  @param {String} payload.shipmentId - cart.shipments.id, not Shipments._id
  @param {String} payload.wishlistId
  @param {String} payload.value - Shipment method
  */
  changeShippingMethod({ commit, dispatch, getters, state }, payload = {}) {
    return new Promise(function (resolve, reject) {
      shoppingCartApi.changeShippingMethod(
        {
          shipmentId: payload.shipmentId,
          wishlistId: payload.wishlistId,
          value: payload.value
        },
        function (err, res) {
          if (err) {
            return reject(err);
          }

          // カート・チェックアウトフローでのみ処理する
          if (state.cartId) {
            const cart = getters.current({
              populate: ['shipments']
            });

            // populateしたcart.shipmentsをDeepコピー
            const shipments = JSON.parse(JSON.stringify(cart.shipments));

            dispatch(
              'shipments/update',
              {
                shipments: shipments,
                shippingMethod: {
                  shipmentId: payload.shipmentId,
                  value: payload.value
                }
              },
              {
                root: true
              }
            );

            commit(shoppingCartTypes.UPDATE_PRICE, {
              cart: res.prices
            });

            // 有効期限を更新
            dispatch('updateCountdownToExpiration', {
              expiration_seconds: res.expiration_seconds
            });
          }

          resolve(res);
        }
      );
    });
  },

  /*
  Fetch the values of the current cart.
  @param {Object} params
  @param {String} params.wishlistId
  @param {Boolean} params.fullValidation
  */
  check(context, payload = {}) {
    return new Promise(function (resolve, reject) {
      shoppingCartApi.check(
        {
          wishlistId: payload.wishlistId,
          fullValidation: payload.fullValidation
        },
        function (err, res) {
          if (err) {
            return reject(err);
          }
          resolve(res);
        }
      );
    });
  },

  /*
  Change shipping address for cart.
  @param {Object} params
  @param {String} params.wishlistId
  */
  changeCartShipping({ commit, dispatch, getters }, payload = {}) {
    return new Promise(function (resolve, reject) {
      shoppingCartApi.changeCartShipping(
        {
          wishlistId: payload.wishlistId
        },
        function (err, res) {
          if (err) {
            return reject(err);
          }

          const cart = getters.current({
            populate: ['shipments']
          });

          // populateしたcart.shipmentsをDeepコピー
          const shipments = JSON.parse(JSON.stringify(cart.shipments));

          dispatch(
            'shipments/update',
            {
              shipments: shipments,
              shipmentMap: res.shipmentMap,
              shippingChargesMap: res.shippingChargesMap
            },
            {
              root: true
            }
          );

          // GAのプロセス送信用に必要
          commit(shoppingCartTypes.ENABLE_TO_PURCHASE);

          // 価格更新
          commit(shoppingCartTypes.UPDATE_PRICE, {
            cart: res.prices
          });

          // 有効期限を更新
          dispatch('updateCountdownToExpiration', {
            expiration_seconds: res.expiration_seconds
          });

          resolve(res);
        }
      );
    });
  },

  /*
  Update point.
  */
  updatePoint({ dispatch, commit, getters }, payload) {
    return new Promise(function (resolve, reject) {
      shoppingCartApi.updatePoint(
        {
          wishlist_id: payload.wishlist_id,
          point: payload.point
        },
        function (err, res) {
          if (err) {
            return reject(err);
          }

          const cart = getters.current({
            populate: ['shipments']
          });

          // populateしたcart.shipmentsをDeepコピー
          const shipments = JSON.parse(JSON.stringify(cart.shipments));

          dispatch(
            'shipments/update',
            {
              shipments: shipments,
              shippingChargesMap: res.shippingChargesMap
            },
            {
              root: true
            }
          );

          commit(shoppingCartTypes.UPDATE_PRICE, {
            cart: res.prices
          });

          // 有効期限を更新
          dispatch('updateCountdownToExpiration', {
            expiration_seconds: res.expiration_seconds
          });

          resolve(res);
        }
      );
    });
  },

  /**
   * おまとめ配送のSKUを分割配送にする
   * @param payload.shipment_id Shipment ID
   * @param payload.sku_id SKU ID
   */
  divideSkuFromShipment({ commit, dispatch }, payload) {
    return new Promise(function (resolve, reject) {
      shoppingCartApi.divideSkuFromShipment(
        {
          cartId: payload.cart_id,
          shipmentId: payload.shipment_id,
          skuId: payload.sku_id
        },
        function (err, res) {
          if (err) {
            return reject(err);
          }

          dispatch(
            'shipments/update',
            {
              shipments: res.cart.shipments,
              shipmentMap: res.shipmentMap,
              shippingChargesMap: res.shippingChargesMap
            },
            {
              root: true
            }
          );

          commit(shoppingCartTypes.UPDATE_SHIPMENTS, {
            shipments: res.cart.shipments.map((shipment) => {
              return shipment._id;
            })
          });

          commit(shoppingCartTypes.UPDATE_PRICE, {
            cart: res.cart
          });

          // 有効期限を更新
          dispatch('updateCountdownToExpiration', {
            expiration_seconds: res.expiration_seconds
          });

          resolve(res);
        }
      );
    });
  },

  /**
   * 分割配送にされたSKUをおまとめ配送に戻す
   * @param payload.shipment_id Shipment ID
   * @param payload.sku_id SKU ID
   */
  combineSkuToShipment({ commit, dispatch }, payload) {
    return new Promise(function (resolve, reject) {
      shoppingCartApi.combineSkuToShipment(
        {
          cartId: payload.cart_id,
          shipmentId: payload.shipment_id,
          skuId: payload.sku_id
        },
        function (err, res) {
          if (err) {
            return reject(err);
          }

          dispatch(
            'shipments/update',
            {
              shipments: res.cart.shipments,
              shipmentMap: res.shipmentMap,
              shippingChargesMap: res.shippingChargesMap
            },
            {
              root: true
            }
          );

          commit(shoppingCartTypes.UPDATE_SHIPMENTS, {
            shipments: res.cart.shipments.map((shipment) => {
              return shipment._id;
            })
          });

          commit(shoppingCartTypes.UPDATE_PRICE, {
            cart: res.cart
          });

          // 有効期限を更新
          dispatch('updateCountdownToExpiration', {
            expiration_seconds: res.expiration_seconds
          });

          resolve(res);
        }
      );
    });
  },

  /**
   * List of Saved for Later / あとで買うリスト
   */
  getSavedForLater() {
    return new Promise(function (resolve, reject) {
      savedForLaterApi
        .getSavedForLater()
        .then((res) => {
          resolve(res);
        })
        .catch((err) => reject(err));
    });
  },

  /**
   * Add to Save for Later / あとで買うに追加
   * @param payload.product_id Product ID
   * @param payload.sku_id ProductSku ID
   */
  addToSavedForLater(context, payload) {
    return new Promise(function (resolve, reject) {
      savedForLaterApi
        .addToSavedForLater({
          product: payload.product_id,
          sku: payload.sku_id
        })
        .then((res) => {
          resolve(res);
        })
        .catch((err) => reject(err));
    });
  },

  /**
   * Delete from Save for Later / あとで買うから削除
   * @param payload.id Saved for later ID
   * @param payload.sku_id ProductSku ID
   */
  deleteFromSavedForLater(context, payload) {
    return new Promise(function (resolve, reject) {
      savedForLaterApi
        .deleteFromSavedForLater({
          id: payload.id
        })
        .then((res) => {
          resolve(res);
        })
        .catch((err) => reject(err));
    });
  }
};

export default {
  namespaced: true,
  state: shoppingCarts.state,
  getters,
  mutations,
  actions
};
